import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createContext } from 'react';

import { CurrencyTypeEnum } from '../model/currency_type.enum';
import { DataLabelEnum } from '../model/data_label.enum';
import { SnapshotTypeEnum } from '../model/snapshot_type';

import { SnapshotRequest } from './model/snapshotData';
import { WebSocketContext } from './websocket-context/websocket-provider';
import { WidgetContext } from '../components/v2/widget-renderer/widget-provider';

type DataType = {
  [SnapshotTypeEnum.CM]: any;
  [SnapshotTypeEnum.EE]: any;
};

type TimestampType = {
  [SnapshotTypeEnum.CM]: string;
  [SnapshotTypeEnum.EE]: string;
};

interface IGetVolProvider {
  data: DataType;
  timestamp: TimestampType;
  currency: CurrencyTypeEnum;
  setCurrency: React.Dispatch<React.SetStateAction<CurrencyTypeEnum>>;
  hasDefaultToken: boolean;
  handleResetSubscription: (snapshotType: SnapshotTypeEnum, token: CurrencyTypeEnum) => void;
}

interface IGetVolParamsProvider {
  children?: ReactNode;
  label?: DataLabelEnum;
  providerKey?: string;
  currency?: CurrencyTypeEnum;
}

const BASE_KEY = {
  [SnapshotTypeEnum.CM]: 'GET_VOL_' + SnapshotTypeEnum.CM,
  [SnapshotTypeEnum.EE]: 'GET_VOL_' + SnapshotTypeEnum.EE,
};

export const GetVolContext = createContext({} as IGetVolProvider);

export const GetVolProvider = (props: IGetVolParamsProvider) => {
  const { providerKey } = props;
  const { subscribeToChannel, unsubscribeFromChannel, sendMessage, isConnected } =
    useContext(WebSocketContext);

  const widgetCtx = useContext(WidgetContext) as Partial<{
    token: CurrencyTypeEnum;
    setToken: React.Dispatch<React.SetStateAction<CurrencyTypeEnum>>;
    hasDefaultToken: boolean;
  }>;

  const [localCurrency, setLocalCurrency] = useState<CurrencyTypeEnum>(
    props.currency || CurrencyTypeEnum.BITCOIN,
  );

  const currency = widgetCtx?.token ?? localCurrency;
  const hasDefaultToken = widgetCtx?.hasDefaultToken ?? Boolean(props.currency);

  const setCurrency: React.Dispatch<React.SetStateAction<CurrencyTypeEnum>> = (value) => {
    if (typeof widgetCtx?.setToken === 'function') {
      widgetCtx.setToken(value as any);
    } else {
      setLocalCurrency((prev) =>
        typeof value === 'function' ? (value as any)(prev) : (value as any),
      );
    }
  };

  const getKey = (snapshotType: SnapshotTypeEnum) => {
    return `${providerKey ? `CUSTOM_${providerKey}` : ''}_${BASE_KEY[snapshotType]}`;
  };

  const wasConnectedRef = useRef({
    [SnapshotTypeEnum.CM]: false,
    [SnapshotTypeEnum.EE]: false,
  });

  const [data, setData] = useState<DataType>({
    [SnapshotTypeEnum.CM]: null,
    [SnapshotTypeEnum.EE]: null,
  });

  const [timestamp, setTimestamp] = useState<TimestampType>({
    [SnapshotTypeEnum.CM]: new Date().toISOString(),
    [SnapshotTypeEnum.EE]: new Date().toISOString(),
  });

  const handleData = (response: any, type: SnapshotTypeEnum) => {
    if (!response) return;
    if (response.result && typeof response.result === 'number') return;

    let timestamp: string;
    let snapshot_time: string;

    if (response.method) {
      timestamp = response?.params?.data?.timestamp;
      snapshot_time = response?.params?.data?.snapshot_time;
    } else {
      timestamp = response?.result?.timestamp;
      snapshot_time = response?.result?.snapshot_time;
    }

    setData((prev) => {
      return {
        ...prev,
        [type]: response,
      };
    });

    setTimestamp((prev) => {
      return {
        ...prev,
        [type]: timestamp ?? snapshot_time,
      };
    });
  };

  const handleGetVols = useCallback(
    (snapshotType: SnapshotTypeEnum, currencyParam?: CurrencyTypeEnum) => {
      const req = new SnapshotRequest(
        getKey(snapshotType),
        currencyParam || currency,
        snapshotType,
        new Date().toISOString(),
      );

      sendMessage(JSON.stringify(req));
      subscribeToChannel(getKey(SnapshotTypeEnum.CM), (message: any) =>
        handleData(message, SnapshotTypeEnum.CM),
      );
      subscribeToChannel(getKey(SnapshotTypeEnum.EE), (message: any) =>
        handleData(message, SnapshotTypeEnum.EE),
      );
    },
    [sendMessage, currency],
  );

  const handleUnsubscribe = useCallback(
    (type: SnapshotTypeEnum) => {
      unsubscribeFromChannel(getKey(type));
    },
    [unsubscribeFromChannel],
  );

  const handleResetSubscription = useCallback(
    (snapshotType: SnapshotTypeEnum, token: CurrencyTypeEnum) => {
      unsubscribeFromChannel(getKey(snapshotType));
      handleGetVols(snapshotType, token);
    },
    [],
  );

  const handleChangeCurrency = useCallback(() => {
    if (isConnected) {
      Object.values(SnapshotTypeEnum).forEach((type: SnapshotTypeEnum) => {
        handleUnsubscribe(type);
        handleGetVols(type);
      });
    }
  }, [isConnected, handleGetVols, handleUnsubscribe]);

  useEffect(() => {
    if (isConnected && currency) {
      handleChangeCurrency();
    }
  }, [currency, isConnected]);

  useEffect(() => {
    if (!currency) {
      setCurrency(CurrencyTypeEnum.BITCOIN);
    }
  }, [currency, hasDefaultToken]);

  useEffect(() => {
    Object.values(SnapshotTypeEnum).forEach((type: SnapshotTypeEnum) => {
      if (wasConnectedRef.current[type]) return;

      if (isConnected) {
        handleUnsubscribe(type);
        handleGetVols(type);
        wasConnectedRef.current = {
          ...wasConnectedRef.current,
          [type]: true,
        };
      }
    });
  }, [isConnected, handleChangeCurrency, handleGetVols, handleUnsubscribe]);

  return (
    <GetVolContext.Provider
      value={{
        data,
        timestamp,
        currency,
        setCurrency,
        hasDefaultToken,
        handleResetSubscription,
      }}
    >
      {props.children}
    </GetVolContext.Provider>
  );
};
